import React, { useCallback, useEffect, useState } from 'react'

import { Copy, Check, ChevronDown, ChevronUp, Clock } from 'lucide-react'

import androidIcon from '@/assets/images/icons/android2.svg'
import appleIcon from '@/assets/images/icons/apple.svg'
import windowsIcon from '@/assets/images/icons/windows.svg'
// Onboarding step images by language
import appImage from '@/assets/images/onboarding/app.png'
import dashboardImage from '@/assets/images/onboarding/dashboard.png'
import enZonesStep2 from '@/assets/images/onboarding/en-zones-step2.gif'
import enZonesStep4Code from '@/assets/images/onboarding/en-zones-step4-code.gif'
import enZonesStep4 from '@/assets/images/onboarding/en-zones-step4.gif'
import esZonesStep2 from '@/assets/images/onboarding/es-zones-step2.gif'
import esZonesStep4Code from '@/assets/images/onboarding/es-zones-step4-code.gif'
import esZonesStep4 from '@/assets/images/onboarding/es-zones-step4.gif'
import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { Schedule } from '@/core/api/entities/schedule'
import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { track } from '@/shared/analytics/analyticsService'
import { useGuideTaskTracking } from '@/shared/analytics/useGuideTaskTracking'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Spinner } from '@/shared/components/atoms/Spinner'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetDescription,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

// Hook to detect if viewport is desktop (>= 1024px)
const useIsDesktop = () => {
	const [isDesktop, setIsDesktop] = useState(
		() => !window.matchMedia('(max-width: 1023px)').matches
	)

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 1023px)')
		const handler = (e: MediaQueryListEvent) => setIsDesktop(!e.matches)

		mediaQuery.addEventListener('change', handler)
		return () => mediaQuery.removeEventListener('change', handler)
	}, [])

	return isDesktop
}

// Detect user's operating system
type Platform = 'windows' | 'macos' | 'ios' | 'android'

const detectPlatform = (): Platform => {
	const userAgent = navigator.userAgent.toLowerCase()
	const platform = navigator.platform?.toLowerCase() || ''

	// Check for iOS (iPhone, iPad, iPod)
	if (/iphone|ipad|ipod/.test(userAgent)) {
		return 'ios'
	}

	// Check for Android
	if (/android/.test(userAgent)) {
		return 'android'
	}

	// Check for macOS
	if (platform.includes('mac') || /macintosh|mac os x/.test(userAgent)) {
		return 'macos'
	}

	// Default to Windows for desktop
	return 'windows'
}

// Responsive Modal wrapper - renders Dialog on desktop, BottomSheet on mobile
interface ResponsiveModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	children: React.ReactNode
	className?: string
}

const ResponsiveModalRoot: React.FC<ResponsiveModalProps> = ({
	open,
	onOpenChange,
	children,
	className,
}) => {
	const isDesktop = useIsDesktop()

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent
					className={`bg-black border-gray-800 sm:max-w-[600px] w-[600px] p-0 pt-6 gap-0 ${className || ''}`}
					showCloseButton={false}
					onInteractOutside={(e) => e.preventDefault()}
					onEscapeKeyDown={(e) => e.preventDefault()}
				>
					<div className="overflow-hidden">{children}</div>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<BottomSheet open={open} onOpenChange={onOpenChange}>
			<BottomSheetContent
				className={`!bg-black border-gray-800 h-auto max-h-[95vh] ${className || ''}`}
				onInteractOutside={(e) => e.preventDefault()}
				onEscapeKeyDown={(e) => e.preventDefault()}
			>
				{children}
			</BottomSheetContent>
		</BottomSheet>
	)
}

// Responsive Title - uses DialogTitle on desktop, BottomSheetTitle on mobile
const ResponsiveTitle: React.FC<{
	children: React.ReactNode
	className?: string
}> = ({ children, className }) => {
	const isDesktop = useIsDesktop()
	const TitleComponent = isDesktop ? DialogTitle : BottomSheetTitle
	return <TitleComponent className={className}>{children}</TitleComponent>
}

// Responsive Description - uses DialogDescription on desktop, BottomSheetDescription on mobile
const ResponsiveDescription: React.FC<{
	children: React.ReactNode
	className?: string
}> = ({ children, className }) => {
	const isDesktop = useIsDesktop()
	const DescComponent = isDesktop ? DialogDescription : BottomSheetDescription
	return <DescComponent className={className}>{children}</DescComponent>
}

// Images mapped by step type
const ONBOARDING_IMAGES = {
	en: {
		step2: enZonesStep2,
		step4: enZonesStep4,
		step4Code: enZonesStep4Code,
	},
	es: {
		step2: esZonesStep2,
		step4: esZonesStep4,
		step4Code: esZonesStep4Code,
	},
} as const

type ImageKey = 'step2' | 'step4' | 'step4Code'

const getOnboardingImage = (language: string, imageKey: ImageKey): string => {
	const baseLanguage = language?.split('-')[0] as 'en' | 'es'
	const images = ONBOARDING_IMAGES[baseLanguage] || ONBOARDING_IMAGES.en
	return images[imageKey]
}

const DOWNLOAD_URLS: Record<Platform, string> = {
	windows: 'https://app.brandtrack.fm/latest.exe',
	macos: 'https://itunes.apple.com/ar/app/brandtrack-player/id1146917730',
	ios: 'https://apps.apple.com/es/app/brandtrack-player/id1429036168',
	android:
		'https://play.google.com/store/apps/details?id=fm.brandtrack.Player&hl=es_BO',
}

const PLATFORM_CONFIG: Record<
	Platform,
	{ icon: string; labelKey: string; alt: string }
> = {
	windows: {
		icon: windowsIcon,
		labelKey: 'modals.zoneOnboarding.download.windows',
		alt: 'Windows',
	},
	macos: {
		icon: appleIcon,
		labelKey: 'modals.zoneOnboarding.download.macos',
		alt: 'macOS',
	},
	ios: {
		icon: appleIcon,
		labelKey: 'modals.zoneOnboarding.download.ios',
		alt: 'iOS',
	},
	android: {
		icon: androidIcon,
		labelKey: 'modals.zoneOnboarding.download.android',
		alt: 'Android',
	},
}

// Handle platform click - use direct navigation for app store links on mobile
// This is more reliable on iOS/Android than target="_blank"
const handlePlatformClick = (
	e: React.MouseEvent<HTMLAnchorElement>,
	url: string
) => {
	const isAppStoreLink =
		url.includes('apps.apple.com') ||
		url.includes('itunes.apple.com') ||
		url.includes('play.google.com')

	// On mobile devices, app store links work better with direct navigation
	if (isAppStoreLink) {
		e.preventDefault()
		window.location.href = url
	}
	// For non-app-store links (like Windows .exe), let the default <a> behavior work
}

// Download button component - shows only OS-specific download button (for "this device" option)
const DownloadButton: React.FC = () => {
	const { t } = useTranslation('zones')
	const detectedPlatform = detectPlatform()
	const config = PLATFORM_CONFIG[detectedPlatform]
	const url = DOWNLOAD_URLS[detectedPlatform]

	return (
		<a
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			className="flex items-center justify-center gap-3 w-full h-12 bg-white text-black rounded-full hover:bg-gray-200 transition-colors font-medium"
			onClick={(e) => {
				track('zone_onboarding_store_clicked', {
					platform: detectedPlatform,
					connection_method: 'this_device',
				})
				handlePlatformClick(e, url)
			}}
		>
			<img src={config.icon} alt={config.alt} className="w-5 h-5" />
			{t(config.labelKey)}
		</a>
	)
}

interface ZoneOnboardingModalProps {
	visible: boolean
	pendingZone: Zone | null
	recommendedSchedules: Schedule[]
	privateSchedulesData: PaginatedResponse<Schedule> | undefined
	assignedScheduleIds: number[]
	onComplete: () => void
	onConnectThisDevice: () => void
	onZoneActivated: () => void
	onScheduleMusic: (scheduleIds: number[]) => void
	refetchZones: () => void
	refetchPrivateSchedules: () => void
	canSkip?: boolean
	onSkip?: () => void
}

interface OnboardingStep {
	titleKey: string
	descriptionKey: string
	type: 'intro' | 'welcome' | 'schedules' | 'activation'
}

const ONBOARDING_STEPS: OnboardingStep[] = [
	{
		titleKey: 'modals.zoneOnboarding.step1.title',
		descriptionKey: 'modals.zoneOnboarding.step1.description',
		type: 'intro',
	},
	{
		titleKey: 'modals.zoneOnboarding.step2.title',
		descriptionKey: 'modals.zoneOnboarding.step2.description',
		type: 'welcome',
	},
	{
		titleKey: 'modals.zoneOnboarding.step3.title',
		descriptionKey: 'modals.zoneOnboarding.step3.description',
		type: 'schedules',
	},
	{
		titleKey: 'modals.zoneOnboarding.step4.title',
		descriptionKey: 'modals.zoneOnboarding.step4.description',
		type: 'activation',
	},
]

const TOTAL_STEPS = 4 // Visual indicator shows 4 steps
const POLLING_INTERVAL = 10000 // 10 seconds
const SCHEDULE_POLLING_INTERVAL = 5000 // 5 seconds for schedule refetch
const MAX_WAIT_TIME = 120000 // 2 minutes max wait

const ZoneOnboardingModal: React.FC<ZoneOnboardingModalProps> = ({
	visible,
	pendingZone,
	recommendedSchedules,
	privateSchedulesData,
	assignedScheduleIds,
	onComplete,
	onConnectThisDevice,
	onZoneActivated,
	onScheduleMusic,
	refetchZones,
	refetchPrivateSchedules,
	canSkip = false,
	onSkip,
}) => {
	const { t, currentLanguage } = useTranslation('zones')
	const { trackTaskById } = useGuideTaskTracking()
	const [currentStep, setCurrentStep] = useState(0)
	const [showCodeView, setShowCodeView] = useState(false)
	const [showStoreIcons, setShowStoreIcons] = useState(false)
	const [copied, setCopied] = useState(false)
	const [selectedSchedules, setSelectedSchedules] = useState<number[]>([])
	const [hasInitializedSelection, setHasInitializedSelection] = useState(false)
	const [showOtherSchedules, setShowOtherSchedules] = useState(false)
	// Store the zone code we're monitoring (to detect when it gets used)
	const [monitoredZoneCode, setMonitoredZoneCode] = useState<string | null>(
		null
	)
	// Track the connection method for analytics
	const [connectionMethod, setConnectionMethod] = useState<
		'this_device' | 'other_device' | null
	>(null)
	// Schedule waiting state
	const [isWaitingForSchedule, setIsWaitingForSchedule] = useState(false)
	const [waitStartTime, setWaitStartTime] = useState<number | null>(null)
	const [hasTimedOut, setHasTimedOut] = useState(false)

	const currentStepData = ONBOARDING_STEPS[currentStep]
	const isSchedulesStep = currentStepData?.type === 'schedules'

	// Rotating messages state for waiting UI
	const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
	const [isAnimatingMessage, setIsAnimatingMessage] = useState(false)

	// Initialize selected schedules with already assigned ones when entering schedules step
	useEffect(() => {
		if (
			isSchedulesStep &&
			!hasInitializedSelection &&
			assignedScheduleIds.length > 0
		) {
			// Filter to only include IDs that are in recommendedSchedules
			const validIds = assignedScheduleIds.filter((id) =>
				recommendedSchedules.some((s) => s.id === id)
			)
			setSelectedSchedules(validIds)
			setHasInitializedSelection(true)
		}
	}, [
		isSchedulesStep,
		hasInitializedSelection,
		assignedScheduleIds,
		recommendedSchedules,
	])

	// Format zone code (just return the code or placeholder)
	const formatZoneCode = (code: string | undefined) => {
		if (!code) return '------'
		return code
	}

	const zoneCode = pendingZone?.code || ''
	const formattedCode = formatZoneCode(monitoredZoneCode || zoneCode)

	// Polling to check if zone was activated
	useEffect(() => {
		if (!showCodeView || !visible) return

		const intervalId = setInterval(() => {
			refetchZones()
		}, POLLING_INTERVAL)

		return () => clearInterval(intervalId)
	}, [showCodeView, visible, refetchZones])

	// Check if zone got activated (when showing code view)
	// The code was used if: pendingZone changed (no longer has our monitored code)
	useEffect(() => {
		if (!showCodeView || !monitoredZoneCode) return

		// If pendingZone no longer has the code we were monitoring, the code was used
		const codeWasUsed = pendingZone?.code !== monitoredZoneCode

		if (codeWasUsed) {
			track('zone_onboarding_zone_activated', {
				zone_code: monitoredZoneCode,
				method: connectionMethod,
			})
			// Track guide task: activate-zone
			trackTaskById('activate-zone')
			setShowCodeView(false)
			setMonitoredZoneCode(null)
			onZoneActivated()
			// Complete the onboarding flow
			onComplete()
		}
	}, [
		pendingZone,
		showCodeView,
		monitoredZoneCode,
		connectionMethod,
		onZoneActivated,
		onComplete,
		trackTaskById,
	])

	// Detect if we have a custom (assigned) schedule from the private schedules endpoint
	const hasPrivateSchedule = !!(
		privateSchedulesData?.data && privateSchedulesData.data.length > 0
	)
	// Fallback to checking recommendedSchedules for a non-public schedule
	const assignedSchedule = recommendedSchedules.find((s) => !s.public)
	const hasAssignedSchedule = hasPrivateSchedule || !!assignedSchedule

	// Start waiting state when entering schedules step without an assigned schedule
	useEffect(() => {
		if (!isSchedulesStep || !visible) {
			// Reset wait state when leaving schedules step
			setIsWaitingForSchedule(false)
			setWaitStartTime(null)
			setHasTimedOut(false)
			return
		}

		// If we already have a schedule or already timed out, don't start waiting
		if (hasAssignedSchedule || hasTimedOut) {
			setIsWaitingForSchedule(false)
			setWaitStartTime(null)
			return
		}

		// Start waiting
		if (!isWaitingForSchedule) {
			setIsWaitingForSchedule(true)
			setWaitStartTime(Date.now())
		}
	}, [
		isSchedulesStep,
		visible,
		hasAssignedSchedule,
		hasTimedOut,
		isWaitingForSchedule,
	])

	// Poll for private schedules while waiting
	useEffect(() => {
		if (!isWaitingForSchedule || !visible || hasTimedOut) return

		const intervalId = setInterval(() => {
			// Only refetch the private schedules endpoint
			refetchPrivateSchedules()
		}, SCHEDULE_POLLING_INTERVAL)

		return () => clearInterval(intervalId)
	}, [isWaitingForSchedule, visible, hasTimedOut, refetchPrivateSchedules])

	// Check for timeout
	useEffect(() => {
		if (!isWaitingForSchedule || !waitStartTime || hasTimedOut) return

		const checkTimeout = () => {
			const elapsed = Date.now() - waitStartTime
			if (elapsed >= MAX_WAIT_TIME) {
				setHasTimedOut(true)
				setIsWaitingForSchedule(false)
				setWaitStartTime(null)
			}
		}

		// Check immediately
		checkTimeout()

		// Check every second
		const timeoutCheckInterval = setInterval(checkTimeout, 1000)

		return () => clearInterval(timeoutCheckInterval)
	}, [isWaitingForSchedule, waitStartTime, hasTimedOut])

	// Stop waiting when schedule appears
	useEffect(() => {
		if (isWaitingForSchedule && hasAssignedSchedule) {
			setIsWaitingForSchedule(false)
			setWaitStartTime(null)
		}
	}, [isWaitingForSchedule, hasAssignedSchedule])

	// Get waiting messages from translations (reusing auth registration messages)
	const waitingMessages = t('modals.zoneOnboarding.step3.waitingMessages', {
		returnObjects: true,
	}) as string[]

	// Rotate messages while waiting
	useEffect(() => {
		if (
			!isWaitingForSchedule ||
			!waitingMessages ||
			waitingMessages.length <= 1
		) {
			return
		}

		// Random delay between 3 and 5 seconds for each message transition
		const getRandomDelay = (min: number, max: number) =>
			Math.floor(Math.random() * (max - min + 1)) + min
		const delay = getRandomDelay(3000, 5000)

		const timer = setTimeout(() => {
			// Start fade out animation
			setIsAnimatingMessage(true)

			// After fade out, change message and fade in
			setTimeout(() => {
				setCurrentMessageIndex((prev) => (prev + 1) % waitingMessages.length)
				setIsAnimatingMessage(false)
			}, 300) // Duration of fade out animation
		}, delay)

		return () => clearTimeout(timer)
	}, [isWaitingForSchedule, currentMessageIndex, waitingMessages])

	// Reset message index when waiting starts
	useEffect(() => {
		if (isWaitingForSchedule) {
			setCurrentMessageIndex(0)
			setIsAnimatingMessage(false)
		}
	}, [isWaitingForSchedule])

	const handleNext = () => {
		track('zone_onboarding_step_completed', {
			step: currentStep + 1,
			total_steps: TOTAL_STEPS,
		})

		setCurrentStep((prev) => prev + 1)
	}

	const handleConnectThisDevice = () => {
		track('zone_onboarding_connect_device', {
			device_type: 'this_device',
			zone_code: zoneCode,
		})
		// Store the code we're monitoring to detect when it gets used
		setMonitoredZoneCode(zoneCode)
		setConnectionMethod('this_device')
		setShowStoreIcons(false)
		setShowCodeView(true)
		onConnectThisDevice()
	}

	const handleConnectOtherDevice = () => {
		track('zone_onboarding_connect_device', {
			device_type: 'other_device',
			zone_code: zoneCode,
		})
		// Store the code we're monitoring to detect when it gets used
		setMonitoredZoneCode(zoneCode)
		setConnectionMethod('other_device')
		setShowStoreIcons(true)
		setShowCodeView(true)
	}

	const handleCopyCode = useCallback(async () => {
		if (!zoneCode) return

		try {
			await navigator.clipboard.writeText(zoneCode)
			setCopied(true)
			track('zone_onboarding_code_copied', {
				zone_code: zoneCode,
			})
			setTimeout(() => setCopied(false), 2000)
		} catch (err) {
			console.error('Failed to copy code:', err)
		}
	}, [zoneCode])

	const handleToggleSchedule = (scheduleId: number) => {
		setSelectedSchedules((prev) =>
			prev.includes(scheduleId)
				? prev.filter((id) => id !== scheduleId)
				: [...prev, scheduleId]
		)
	}

	const handleSkip = () => {
		track('zone_onboarding_skipped', {
			step: currentStep + 1,
			total_steps: TOTAL_STEPS,
		})
		onSkip?.()
	}

	const handleScheduleMusic = () => {
		// Always preserve all previously assigned schedules alongside the user's new selection.
		// Using assignedScheduleIds directly (from zones API) avoids a race condition where
		// the webhook-generated private schedule may not yet be present in recommendedSchedules,
		// which would cause Zones.tsx handleScheduleMusic to compute it as "to remove".
		const finalSelection = [
			...new Set([...assignedScheduleIds, ...selectedSchedules]),
		]

		track('zone_onboarding_schedule_music', {
			selected_schedules: finalSelection,
			schedule_count: finalSelection.length,
		})
		// Track guide task: assign-content
		trackTaskById('assign-content')
		onScheduleMusic(finalSelection)
		// Move to activation step
		setCurrentStep((prev) => prev + 1)
	}

	// Render the code view (for both "this device" and "other device") - shown during activation step
	if (showCodeView) {
		return (
			<ResponsiveModalRoot open={visible} onOpenChange={() => {}}>
				<div className="px-6 pb-8 space-y-6">
					{/* Step Image/GIF */}
					<div className="flex justify-center">
						<div className="w-full max-w-full rounded-xl overflow-hidden bg-[#1a1a1a]">
							<img
								src={getOnboardingImage(currentLanguage, 'step4Code')}
								alt="Zone activation code"
								className="w-full h-auto max-w-full object-contain"
							/>
						</div>
					</div>

					{/* Title and Description */}
					<div className="space-y-3 text-center">
						<ResponsiveTitle className="text-white text-2xl">
							{t('modals.zoneOnboarding.step4.title')}
						</ResponsiveTitle>
						<ResponsiveDescription className="text-gray-400 text-base leading-relaxed whitespace-pre-line">
							{t('modals.zoneOnboarding.step4.description')}
						</ResponsiveDescription>
					</div>

					{/* Zone Code Display */}
					<div className="flex items-center justify-center gap-3">
						<div className="flex items-center gap-1">
							{formattedCode.split('').map((char, index) => (
								<div
									key={index}
									className="w-10 h-12 flex items-center justify-center text-2xl font-bold text-white bg-[#2a2a2a] rounded-lg"
								>
									{char}
								</div>
							))}
						</div>
						<Button
							variant="outline"
							size="icon"
							onClick={handleCopyCode}
							className="h-12 w-12 rounded-full border-gray-600 text-white hover:bg-gray-800"
						>
							{copied ? (
								<Check className="w-5 h-5" />
							) : (
								<Copy className="w-5 h-5" />
							)}
						</Button>
					</div>

					{/* Instructions */}
					<p className="text-gray-400 text-sm text-center">
						{t('modals.zoneOnboarding.otherDevice.instructionsBefore')}{' '}
						<a
							href="https://app.brandtrack.fm"
							target="_blank"
							rel="noopener noreferrer"
							className="text-white underline hover:text-gray-300"
						>
							https://app.brandtrack.fm
						</a>{' '}
						{t('modals.zoneOnboarding.otherDevice.instructionsAfter')}
					</p>

					{/* Download button - Only show for "this device" option */}
					{!showStoreIcons && <DownloadButton />}

					{/* Step indicators */}
					<div className="flex justify-center gap-2">
						{Array.from({ length: TOTAL_STEPS }).map((_, index) => (
							<div
								key={index}
								className={`h-2 rounded-full transition-all duration-300 ${
									index === 3 ? 'w-6 bg-white' : 'w-2 bg-gray-600'
								}`}
							/>
						))}
					</div>

					{/* Waiting Button */}
					<Button
						disabled
						className="w-full h-12 text-base font-medium rounded-full bg-gray-700 text-gray-400 cursor-not-allowed"
					>
						{t('modals.zoneOnboarding.otherDevice.waiting')}
					</Button>

					{/* Skip Button - Only for non-trial accounts */}
					{canSkip && onSkip && (
						<Button
							onClick={handleSkip}
							variant="ghost"
							className="w-full h-10 text-sm font-medium text-gray-400 hover:text-white hover:bg-transparent"
						>
							{t('modals.zoneOnboarding.skip')}
						</Button>
					)}
				</div>
			</ResponsiveModalRoot>
		)
	}

	// Step 1: Intro - Two components, one system
	if (currentStepData.type === 'intro') {
		// Feature tag component - same style as IdBadge
		const FeatureTag: React.FC<{ children: React.ReactNode }> = ({
			children,
		}) => (
			<div className="inline-flex items-center rounded-md border border-border bg-shade text-muted-foreground text-xs font-medium px-2 py-1">
				{children}
			</div>
		)

		return (
			<ResponsiveModalRoot open={visible} onOpenChange={() => {}}>
				<div className="px-4 sm:px-6 pb-6 sm:pb-8 space-y-4 sm:space-y-6">
					{/* Mobile Layout: Two images side by side (above heading) */}
					<div className="lg:hidden">
						<div className="grid grid-cols-2 gap-3">
							{/* Dashboard Image */}
							<div className="relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-gray-800">
								<img
									src={dashboardImage}
									alt={t('modals.zoneOnboarding.step1.myBrandtrack.title')}
									className="w-full h-auto object-contain"
								/>
								<div className="absolute bottom-2 left-2 right-2">
									<span className="text-[#66CCB3] text-xs font-semibold bg-black/70 px-2 py-1 rounded">
										{t('modals.zoneOnboarding.step1.myBrandtrack.title')}
									</span>
								</div>
							</div>
							{/* App Image */}
							<div className="relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-gray-800">
								<img
									src={appImage}
									alt={t('modals.zoneOnboarding.step1.player.title')}
									className="w-full h-auto object-contain"
								/>
								<div className="absolute bottom-2 left-2 right-2">
									<span className="text-[#B794F6] text-xs font-semibold bg-black/70 px-2 py-1 rounded">
										{t('modals.zoneOnboarding.step1.player.title')}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Desktop Layout: Images only (above heading) */}
					<div className="hidden lg:flex lg:flex-row gap-8 items-start justify-center">
						{/* Dashboard Image */}
						<div className="flex-1 max-w-[50%]">
							<div className="relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-gray-800">
								<img
									src={dashboardImage}
									alt={t('modals.zoneOnboarding.step1.myBrandtrack.title')}
									className="w-full h-auto object-contain"
								/>
							</div>
						</div>
						{/* App Image */}
						<div className="flex-1 max-w-[280px] xl:max-w-[320px]">
							<div className="relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-gray-800">
								<img
									src={appImage}
									alt={t('modals.zoneOnboarding.step1.player.title')}
									className="w-full h-auto object-contain"
								/>
							</div>
						</div>
					</div>

					{/* Title and Description */}
					<div className="space-y-3 text-center">
						<ResponsiveTitle className="text-white text-2xl">
							{t(currentStepData.titleKey)}
						</ResponsiveTitle>
						<ResponsiveDescription className="text-gray-400 text-base leading-relaxed whitespace-pre-line lg:block hidden">
							{t(currentStepData.descriptionKey)}
						</ResponsiveDescription>
						{/* Mobile: Common description */}
						<p className="text-gray-400 text-base leading-relaxed whitespace-pre-line lg:hidden">
							{t('modals.zoneOnboarding.step1.mobileDescription')}
						</p>
					</div>

					{/* Desktop Layout: Titles and tags below heading */}
					<div className="hidden lg:flex lg:flex-row gap-8 items-start justify-center">
						{/* My Brandtrack Section */}
						<div className="flex-1 max-w-[50%] flex flex-col gap-4">
							<h3 className="text-[#66CCB3] text-2xl font-semibold">
								{t('modals.zoneOnboarding.step1.myBrandtrack.title')}
							</h3>
							<div className="flex flex-col items-start gap-1.5">
								<FeatureTag>
									{t('modals.zoneOnboarding.step1.myBrandtrack.tags.zones')}
								</FeatureTag>
								<FeatureTag>
									{t(
										'modals.zoneOnboarding.step1.myBrandtrack.tags.monitoring'
									)}
								</FeatureTag>
								<FeatureTag>
									{t('modals.zoneOnboarding.step1.myBrandtrack.tags.rollout')}
								</FeatureTag>
							</div>
						</div>

						{/* Brandtrack Player App Section */}
						<div className="flex-1 max-w-[280px] xl:max-w-[320px] flex flex-col gap-4">
							<h3 className="text-[#B794F6] text-2xl font-semibold">
								{t('modals.zoneOnboarding.step1.player.title')}
							</h3>
							<div className="flex flex-col items-start gap-1.5">
								<FeatureTag>
									{t('modals.zoneOnboarding.step1.player.tags.playback')}
								</FeatureTag>
								<FeatureTag>
									{t('modals.zoneOnboarding.step1.player.tags.permissions')}
								</FeatureTag>
								<FeatureTag>
									{t('modals.zoneOnboarding.step1.player.tags.staff')}
								</FeatureTag>
							</div>
						</div>
					</div>

					{/* Step indicators */}
					<div className="flex justify-center gap-2">
						{Array.from({ length: TOTAL_STEPS }).map((_, index) => (
							<div
								key={index}
								className={`h-2 rounded-full transition-all duration-300 ${
									index === currentStep ? 'w-6 bg-white' : 'w-2 bg-gray-600'
								}`}
							/>
						))}
					</div>

					{/* Next Button */}
					<Button
						onClick={handleNext}
						className="w-full h-12 text-base font-medium rounded-full bg-white text-black hover:bg-gray-200"
					>
						{t('modals.zoneOnboarding.next')}
					</Button>

					{/* Skip Button - Only for non-trial accounts */}
					{canSkip && onSkip && (
						<Button
							onClick={handleSkip}
							variant="ghost"
							className="w-full h-10 text-sm font-medium text-gray-400 hover:text-white hover:bg-transparent"
						>
							{t('modals.zoneOnboarding.skip')}
						</Button>
					)}
				</div>
			</ResponsiveModalRoot>
		)
	}

	// Step 2: Welcome to My Brandtrack
	if (currentStepData.type === 'welcome') {
		return (
			<ResponsiveModalRoot open={visible} onOpenChange={() => {}}>
				<div className="px-6 pb-8 space-y-6">
					{/* Step Image/GIF */}
					<div className="flex justify-center">
						<div className="w-full max-w-full rounded-xl overflow-hidden bg-[#1a1a1a]">
							<img
								src={getOnboardingImage(currentLanguage, 'step2')}
								alt="Welcome to My Brandtrack"
								className="w-full h-auto max-w-full object-contain"
							/>
						</div>
					</div>

					{/* Title and Description */}
					<div className="space-y-3 text-center">
						<ResponsiveTitle className="text-white text-2xl">
							{t(currentStepData.titleKey)}
						</ResponsiveTitle>
						<ResponsiveDescription className="text-gray-400 text-base leading-relaxed whitespace-pre-line">
							{t(currentStepData.descriptionKey)}
						</ResponsiveDescription>
					</div>

					{/* Step indicators */}
					<div className="flex justify-center gap-2">
						{Array.from({ length: TOTAL_STEPS }).map((_, index) => (
							<div
								key={index}
								className={`h-2 rounded-full transition-all duration-300 ${
									index === currentStep ? 'w-6 bg-white' : 'w-2 bg-gray-600'
								}`}
							/>
						))}
					</div>

					{/* Next Button */}
					<Button
						onClick={handleNext}
						className="w-full h-12 text-base font-medium rounded-full bg-white text-black hover:bg-gray-200"
					>
						{t('modals.zoneOnboarding.next')}
					</Button>

					{/* Skip Button - Only for non-trial accounts */}
					{canSkip && onSkip && (
						<Button
							onClick={handleSkip}
							variant="ghost"
							className="w-full h-10 text-sm font-medium text-gray-400 hover:text-white hover:bg-transparent"
						>
							{t('modals.zoneOnboarding.skip')}
						</Button>
					)}
				</div>
			</ResponsiveModalRoot>
		)
	}

	// Step 3: Schedules
	if (currentStepData.type === 'schedules') {
		// Get the custom (private) schedule - prioritize from privateSchedulesData
		const assignedSchedule =
			privateSchedulesData?.data?.[0] ||
			recommendedSchedules.find((s) => !s.public)
		const otherSchedules = recommendedSchedules.filter(
			(s) => s.id !== assignedSchedule?.id
		)

		const renderScheduleCard = (schedule: Schedule) => {
			const cover =
				schedule.metadata?.mediumCovers?.[0] ||
				schedule.metadata?.smallCovers?.[0]
			const isSelected = selectedSchedules.includes(schedule.id)

			return (
				<div
					key={schedule.id}
					className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
						isSelected ? 'bg-white/10' : 'bg-[#1a1a1a] hover:bg-[#222]'
					}`}
					onClick={() => handleToggleSchedule(schedule.id)}
				>
					{/* Cover Image */}
					<div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
						{cover ? (
							<img
								src={cover}
								alt={schedule.title}
								className="w-full h-full object-cover"
							/>
						) : (
							<div className="w-full h-full bg-gradient-to-br from-violet-600 to-purple-800" />
						)}
					</div>

					{/* Info */}
					<div className="flex-1 min-w-0">
						<p className="text-white font-medium truncate">{schedule.title}</p>
						{schedule.description && (
							<p className="text-gray-400 text-sm truncate mt-0.5">
								{schedule.description}
							</p>
						)}
					</div>

					{/* Checkbox */}
					<Checkbox
						checked={isSelected}
						onCheckedChange={() => handleToggleSchedule(schedule.id)}
						className="border-gray-600 data-[state=checked]:bg-white data-[state=checked]:border-white"
					/>
				</div>
			)
		}

		return (
			<ResponsiveModalRoot open={visible} onOpenChange={() => {}}>
				<div className="px-6 pb-8 space-y-5">
					{/* Title and Description */}
					<div className="space-y-2">
						<ResponsiveTitle className="text-white text-2xl text-left">
							{t(currentStepData.titleKey)}
						</ResponsiveTitle>
						<ResponsiveDescription className="text-gray-400 text-base leading-relaxed whitespace-pre-line text-left">
							{t(currentStepData.descriptionKey)}
						</ResponsiveDescription>
					</div>

					{/* Assigned Schedule - Featured (always included, non-removable) OR Waiting State */}
					{isWaitingForSchedule ? (
						<div className="space-y-4">
							<div className="rounded-xl bg-card ring-1 ring-border p-8 flex flex-col items-center justify-center min-h-[240px]">
								<Spinner size="lg" className="text-white mb-6" />

								{/* Rotating message with fade animation */}
								<p
									className={`text-white/90 text-base text-center max-w-md transition-opacity duration-300 ${
										isAnimatingMessage ? 'opacity-0' : 'opacity-100'
									}`}
								>
									{waitingMessages[currentMessageIndex]}
								</p>
							</div>
						</div>
					) : (
						assignedSchedule &&
						(() => {
							const ALL_DAYS = [
								'MO',
								'TU',
								'WE',
								'TH',
								'FR',
								'SA',
								'SU',
							] as const

							// Clean slot name: remove prefixes like "BrandName - SubName - "
							// Keep only the last meaningful segment
							const cleanSlotName = (name: string) => {
								const parts = name.split(' - ')
								return parts.length > 1 ? parts[parts.length - 1] : name
							}

							// Parse timeslots from the schedule
							const parsedSlots = (assignedSchedule.timeslots || [])
								.map((timeslot) => {
									const veventLines = timeslot.vevent.split('\n')
									let dtstart = ''
									let dtend = ''
									let rruleStr = ''
									let durationStr = ''

									veventLines.forEach((line) => {
										if (line.startsWith('DTSTART:'))
											dtstart = line.substring('DTSTART:'.length)
										else if (line.startsWith('DTEND:'))
											dtend = line.substring('DTEND:'.length)
										else if (line.startsWith('RRULE:'))
											rruleStr = line.substring('RRULE:'.length)
										else if (line.startsWith('DURATION:'))
											durationStr = line.substring('DURATION:'.length)
									})

									const dtstartClean = dtstart.replace(/Z$/, '')
									const startHour = parseInt(dtstartClean.substring(9, 11)) || 0
									const startMinute =
										parseInt(dtstartClean.substring(11, 13)) || 0
									let endHour = startHour
									let endMinute = startMinute

									if (dtend) {
										const dtendClean = dtend.replace(/Z$/, '')
										endHour = parseInt(dtendClean.substring(9, 11)) || 0
										endMinute = parseInt(dtendClean.substring(11, 13)) || 0
									} else if (durationStr) {
										const hm = durationStr.match(/(\d+)H/)
										const mm = durationStr.match(/(\d+)M/)
										endMinute = startMinute + (mm ? parseInt(mm[1]) : 0)
										endHour =
											startHour +
											(hm ? parseInt(hm[1]) : 0) +
											Math.floor(endMinute / 60)
										endMinute = endMinute % 60
									}

									const days: string[] = []
									if (rruleStr) {
										const bydayMatch = rruleStr.match(/BYDAY=([^;]+)/)
										if (bydayMatch) days.push(...bydayMatch[1].split(','))
									}
									if (days.length === 0 || days.length === 7) {
										days.length = 0
										days.push(...ALL_DAYS)
									}

									// Color from playlist IDs (same palette as schedule calendar)
									const PALETTE = [
										'#BF199B',
										'#5F39BF',
										'#00B4BF',
										'#31AC9E',
										'#C4A31D',
									]
									const pIds =
										timeslot.timeslotContents?.[0]?.playlists
											?.map((p) => p.id.toString())
											.sort()
											.join('|') || `S${timeslot.id}`
									let hash = 0
									for (let i = 0; i < pIds.length; i++) {
										hash = pIds.charCodeAt(i) + ((hash << 5) - hash)
										hash = hash & hash
									}

									return {
										name: cleanSlotName(timeslot.name),
										days: days.sort(
											(a, b) =>
												ALL_DAYS.indexOf(a as (typeof ALL_DAYS)[number]) -
												ALL_DAYS.indexOf(b as (typeof ALL_DAYS)[number])
										),
										startTime: `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`,
										endTime: `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`,
										color: PALETTE[Math.abs(hash) % PALETTE.length],
										startMinutes: startHour * 60 + startMinute,
									}
								})
								.sort((a, b) => a.startMinutes - b.startMinutes)

							// Group by day pattern
							const dayGroups = parsedSlots.reduce<
								Record<string, typeof parsedSlots>
							>((acc, slot) => {
								const key = slot.days.join(',')
								if (!acc[key]) acc[key] = []
								acc[key].push(slot)
								return acc
							}, {})

							return (
								<div className="space-y-2">
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
										{t('modals.zoneOnboarding.step3.assignedSchedule')}
									</p>
									<div className="rounded-xl bg-[#0c1a1e] ring-1 ring-white/10 overflow-hidden max-h-[35vh] overflow-y-auto">
										{/* Header */}
										<div className="flex items-center gap-2 px-4 py-2.5">
											<Clock className="w-3.5 h-3.5 text-gray-400" />
											<span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">
												{t('modals.zoneOnboarding.step3.scheduleLabel')}
											</span>
										</div>

										{/* Day groups */}
										<div className="space-y-3 px-4 pb-4">
											{Object.entries(dayGroups).map(([dayKey, slots]) => {
												const activeDays = dayKey.split(',')

												return (
													<div key={dayKey} className="space-y-2">
														{/* Day letters */}
														<div className="flex gap-0.5">
															{ALL_DAYS.map((day) => {
																const isActive = activeDays.includes(day)
																return (
																	<span
																		key={day}
																		className={`text-[10px] font-bold w-4 text-center ${
																			isActive
																				? 'text-[#e8956a]'
																				: 'text-white/15'
																		}`}
																	>
																		{t(
																			`modals.zoneOnboarding.step3.dayLetters.${day}`
																		)}
																	</span>
																)
															})}
														</div>

														{/* Time blocks - vertical on mobile, horizontal on desktop */}
														<div className="flex flex-col lg:flex-row gap-1">
															{slots.map((slot, idx) => (
																<div
																	key={idx}
																	className="lg:flex-1 min-w-0 rounded-md px-2.5 py-2"
																	style={{ backgroundColor: slot.color }}
																>
																	<p className="text-white text-[11px] font-medium truncate">
																		{slot.name}
																	</p>
																	<p className="text-white/60 text-[10px]">
																		{slot.startTime} - {slot.endTime}
																	</p>
																</div>
															))}
														</div>
													</div>
												)
											})}
										</div>
									</div>
								</div>
							)
						})()
					)}

					{/* Other Schedules - Expandable (hidden during waiting) */}
					{!isWaitingForSchedule && otherSchedules.length > 0 && (
						<div className="space-y-2">
							<button
								onClick={() => setShowOtherSchedules(!showOtherSchedules)}
								className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
							>
								{t('modals.zoneOnboarding.step3.otherSchedules')}
								{showOtherSchedules ? (
									<ChevronUp className="w-4 h-4" />
								) : (
									<ChevronDown className="w-4 h-4" />
								)}
							</button>

							{showOtherSchedules && (
								<div className="space-y-2 max-h-[30vh] overflow-y-auto">
									{otherSchedules.map((schedule) =>
										renderScheduleCard(schedule)
									)}
								</div>
							)}
						</div>
					)}

					{/* Step indicators */}
					<div className="flex justify-center gap-2">
						{Array.from({ length: TOTAL_STEPS }).map((_, index) => (
							<div
								key={index}
								className={`h-2 rounded-full transition-all duration-300 ${
									index === currentStep ? 'w-6 bg-white' : 'w-2 bg-gray-600'
								}`}
							/>
						))}
					</div>

					{/* Schedule Button - always enabled since assigned schedule is always included */}
					<Button
						onClick={handleScheduleMusic}
						disabled={isWaitingForSchedule}
						className="w-full h-12 text-base font-medium rounded-full bg-white text-black hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{t('modals.zoneOnboarding.step3.scheduleButton')}
					</Button>

					{/* Skip Button - Only for non-trial accounts, hidden during wait */}
					{canSkip && onSkip && !isWaitingForSchedule && (
						<Button
							onClick={handleSkip}
							variant="ghost"
							className="w-full h-10 text-sm font-medium text-gray-400 hover:text-white hover:bg-transparent"
						>
							{t('modals.zoneOnboarding.skip')}
						</Button>
					)}
				</div>
			</ResponsiveModalRoot>
		)
	}

	// Step 4: Activation
	return (
		<ResponsiveModalRoot open={visible} onOpenChange={() => {}}>
			<div className="px-6 pb-8 space-y-6">
				{/* Step Image/GIF */}
				<div className="flex justify-center">
					<div className="w-full max-w-full rounded-xl overflow-hidden bg-[#1a1a1a]">
						<img
							src={getOnboardingImage(currentLanguage, 'step4')}
							alt="Activate your player"
							className="w-full h-auto max-w-full object-contain"
						/>
					</div>
				</div>

				{/* Title and Description */}
				<div className="space-y-3 text-center">
					<ResponsiveTitle className="text-white text-2xl">
						{t(currentStepData.titleKey)}
					</ResponsiveTitle>
					<ResponsiveDescription className="text-gray-400 text-base leading-relaxed whitespace-pre-line">
						{t(currentStepData.descriptionKey)}
					</ResponsiveDescription>
				</div>

				{/* Step indicators */}
				<div className="flex justify-center gap-2">
					{Array.from({ length: TOTAL_STEPS }).map((_, index) => (
						<div
							key={index}
							className={`h-2 rounded-full transition-all duration-300 ${
								index === currentStep ? 'w-6 bg-white' : 'w-2 bg-gray-600'
							}`}
						/>
					))}
				</div>

				{/* Action Buttons */}
				<div className="space-y-3">
					<Button
						onClick={handleConnectThisDevice}
						className="w-full h-12 text-base font-medium rounded-full bg-white text-black hover:bg-gray-200"
					>
						{t('modals.zoneOnboarding.step4.connectThisDevice')}
					</Button>
					<Button
						onClick={handleConnectOtherDevice}
						variant="outline"
						className="w-full h-12 text-base font-medium rounded-full border-gray-600 text-white hover:bg-gray-800"
					>
						{t('modals.zoneOnboarding.step4.connectOtherDevice')}
					</Button>

					{/* Skip Button - Only for non-trial accounts */}
					{canSkip && onSkip && (
						<Button
							onClick={handleSkip}
							variant="ghost"
							className="w-full h-10 text-sm font-medium text-gray-400 hover:text-white hover:bg-transparent"
						>
							{t('modals.zoneOnboarding.skip')}
						</Button>
					)}
				</div>
			</div>
		</ResponsiveModalRoot>
	)
}

export default ZoneOnboardingModal
